import { createFeatureFlags } from 'src/v2/shared/feature-flags';
import { GravionStorageKeys } from 'src/v2/shared/storage/keys';
import { Stage, CURRENT_STAGE } from 'src/contstant/apiEndpoints';

// ---------------------------------------------------------------------------
// Flag keys — const object for type safety
// ---------------------------------------------------------------------------

export const FEATURE_FLAGS = {
  enableEnhancedResponseTypes: 'enableEnhancedResponseTypes',
  enableV2Desktop: 'enableV2Desktop',
  enableSlackNotifications: 'enableSlackNotifications',
  enableAuditWhiz: 'enableAuditWhiz',
  enableAuditInsights: 'enableAuditInsights',
  enableMobileAstra: 'enableMobileAstra',
  enableDesktopAstra: 'enableDesktopAstra',
  enableRiskEngine: 'enableRiskEngine',
} as const;

export type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

// ---------------------------------------------------------------------------
// Human-readable descriptions (shown in the dev toggle panel)
// ---------------------------------------------------------------------------

export const featureFlagDescriptions: Record<FeatureFlagKey, string> = {
  [FEATURE_FLAGS.enableEnhancedResponseTypes]:
    'Enhanced Response Types (ERT) for questions',
  [FEATURE_FLAGS.enableV2Desktop]: 'Enable Stencil UI',
  [FEATURE_FLAGS.enableSlackNotifications]:
    'Slack notifications opt-in (profile preferences)',
  [FEATURE_FLAGS.enableAuditWhiz]:
    'AuditWhiz — AI-powered question generation with Astra',
  [FEATURE_FLAGS.enableAuditInsights]:
    'Audit Insights tab on the audit summary page (Powered by Astra AI)',
  [FEATURE_FLAGS.enableMobileAstra]:
    'Chat with Astra on the mobile shell (/chat route and nav entry)',
  [FEATURE_FLAGS.enableDesktopAstra]:
    'Stencil Astra panel in place of the Cloudscape copilot panel on desktop',
  [FEATURE_FLAGS.enableRiskEngine]:
    'Risk Engine location metrics page and navigation entry',
};

// ---------------------------------------------------------------------------
// Defaults — all false per shared-lib convention.
// Stage-based values are passed as apiFlags at the Provider level.
// ---------------------------------------------------------------------------

const defaultFeatureFlags: Record<FeatureFlagKey, boolean> = {
  [FEATURE_FLAGS.enableEnhancedResponseTypes]: false,
  [FEATURE_FLAGS.enableV2Desktop]: false,
  [FEATURE_FLAGS.enableSlackNotifications]: false,
  [FEATURE_FLAGS.enableAuditWhiz]: false,
  [FEATURE_FLAGS.enableAuditInsights]: false,
  [FEATURE_FLAGS.enableMobileAstra]: false,
  [FEATURE_FLAGS.enableDesktopAstra]: false,
  [FEATURE_FLAGS.enableRiskEngine]: false,
};

// ---------------------------------------------------------------------------
// Stage-based flag values — passed as apiFlags to the Provider.
// These represent what the environment enables by default (medium priority).
// In non-prod, local storage overrides can still flip them.
// ---------------------------------------------------------------------------

export const FEATURE_FLAGS_BY_STAGE: Record<Stage, Record<FeatureFlagKey, boolean>> = {
  [Stage.TEST]: {
    enableEnhancedResponseTypes: true,
    enableV2Desktop: false,
    enableSlackNotifications: true,
    enableAuditWhiz: true,
    enableAuditInsights: true,
    enableMobileAstra: true,
    enableDesktopAstra: true,
    enableRiskEngine: true,
  },
  [Stage.BETA]: {
    enableEnhancedResponseTypes: true,
    enableV2Desktop: false,
    enableSlackNotifications: true,
    enableAuditWhiz: true,
    enableAuditInsights: true,
    enableMobileAstra: true,
    enableDesktopAstra: true,
    enableRiskEngine: true,
  },
  [Stage.GAMMA]: {
    enableEnhancedResponseTypes: true,
    enableV2Desktop: false,
    enableSlackNotifications: true,
    enableAuditWhiz: true,
    enableAuditInsights: false,
    enableMobileAstra: true,
    enableDesktopAstra: true,
    enableRiskEngine: false,
  },
  [Stage.PROD]: {
    enableEnhancedResponseTypes: true,
    enableV2Desktop: false,
    enableSlackNotifications: true,
    enableAuditWhiz: false,
    enableAuditInsights: false,
    enableMobileAstra: true,
    enableDesktopAstra: true,
    enableRiskEngine: false,
  },
};

/**
 * Returns the stage-based flag values for the current environment.
 * Used as apiFlags in the Provider.
 */
export const getFeatureFlagsForStage = (): Record<FeatureFlagKey, boolean> => {
  return FEATURE_FLAGS_BY_STAGE[CURRENT_STAGE] ?? FEATURE_FLAGS_BY_STAGE[Stage.BETA];
};

// ---------------------------------------------------------------------------
// Create the feature flag system
// ---------------------------------------------------------------------------

const {
  Provider: FeatureFlagProvider,
  useFeatureFlagContext,
  useFeatureFlag,
  useFeatureFlags,
} = createFeatureFlags<FeatureFlagKey>({
  flags: FEATURE_FLAGS,
  descriptions: featureFlagDescriptions,
  defaults: defaultFeatureFlags,
  storageKey: GravionStorageKeys.featureFlags,
  isProduction: () => CURRENT_STAGE === Stage.PROD,
  isDevMode: () => CURRENT_STAGE !== Stage.PROD,
});

export { FeatureFlagProvider, useFeatureFlagContext, useFeatureFlag, useFeatureFlags };

// Legacy alias — the old provider was named FeatureFlagsProvider (plural).
// Keep the export so index.tsx doesn't need an import change.
export { FeatureFlagProvider as FeatureFlagsProvider };

// Legacy type alias — v2/shared/auth/useFeatureFlags.ts re-exports this type.
// In the new system the equivalent is Record<FeatureFlagKey, boolean>.
export type FeatureFlags = Record<FeatureFlagKey, boolean>;
